import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import styles from './MicrositeSeo.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw, FileSearch, ArrowRightLeft, Map, ArrowRight } from 'lucide-react'

import { MicrositeUnsavedTabsContext } from '../MicrositeShell/MicrositeShell'
import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import TextInput from '../components/TextInput/TextInput'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeSerpPreview from '../components/MicrositeSerpPreview/MicrositeSerpPreview'
import MicrositeVersionDrawer from '../components/MicrositeVersionDrawer/MicrositeVersionDrawer'

const ROBOTS_OPTIONS = [
    { value: 'index, follow', label: 'Index, follow (recommended)' },
    { value: 'noindex, follow', label: 'Noindex, follow' },
    { value: 'index, nofollow', label: 'Index, nofollow' },
    { value: 'noindex, nofollow', label: 'Noindex, nofollow' }
]

const TWITTER_CARD_OPTIONS = [
    { value: 'summary_large_image', label: 'Large image' },
    { value: 'summary', label: 'Summary' }
]

const EMPTY_FORM = {
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    ogTitle: '',
    ogDescription: '',
    ogImage: '',
    twitterCard: 'summary_large_image',
    robotsDirective: 'index, follow',
    canonicalUrl: ''
}

const MicrositeSeo = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])
    const { micrositeStore } = useContext(MicrositeUnsavedTabsContext)

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [historyOpen, setHistoryOpen] = useState(false)

    const [form, setForm] = useState(EMPTY_FORM)
    const [original, setOriginal] = useState(EMPTY_FORM)

    const isDirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadSeo = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreSeo')
        if (res?.status === 'success') {
            const s = res.response?.seoConfig || {}
            const next = {
                metaTitle: s.metaTitle || '',
                metaDescription: s.metaDescription || '',
                metaKeywords: s.metaKeywords || '',
                ogTitle: s.ogTitle || '',
                ogDescription: s.ogDescription || '',
                ogImage: s.ogImage || '',
                twitterCard: s.twitterCard || 'summary_large_image',
                robotsDirective: s.robotsDirective || 'index, follow',
                canonicalUrl: s.canonicalUrl || ''
            }
            setForm(next)
            setOriginal(next)
        } else {
            showToast('Could not load SEO settings. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadSeo()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }))

    const handleSave = async () => {
        setSaving(true)
        const res = await post('micrositeUpdateStoreSeoConfig', { ...form })
        setSaving(false)

        if (res?.status === 'success') {
            setOriginal(form)
            showToast('SEO settings saved successfully')
        } else {
            showToast('Could not save SEO settings. Please try again.', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading SEO settings…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="SEO settings"
                subtitle="Default search engine and social sharing metadata for your microsite."
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onHistory={() => setHistoryOpen(true)}
            />

            <div className={styles.layout}>
                <div className={styles.formCol}>
                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>Search metadata</h2>
                        <p className={styles.groupDesc}>Shown in Google search results.</p>

                        <TextInput
                            label="Meta title"
                            value={form.metaTitle}
                            onChange={(v) => update('metaTitle', v)}
                            placeholder="e.g. Glam Studio — Premium Salon in Delhi"
                            maxLength={60}
                            showCharCount
                        />
                        <TextInput
                            label="Meta description"
                            value={form.metaDescription}
                            onChange={(v) => update('metaDescription', v)}
                            placeholder="A short, compelling description of your salon for search results"
                            maxLength={160}
                            showCharCount
                        />
                        <TextInput
                            label="Meta keywords"
                            value={form.metaKeywords}
                            onChange={(v) => update('metaKeywords', v)}
                            placeholder="salon, hair spa, bridal makeup, delhi"
                            helperText="Comma-separated — minor SEO impact but still used by some engines"
                        />
                        <SelectInput
                            label="Robots directive"
                            value={form.robotsDirective}
                            onChange={(v) => update('robotsDirective', v)}
                            options={ROBOTS_OPTIONS}
                        />
                        <TextInput
                            label="Canonical URL"
                            value={form.canonicalUrl}
                            onChange={(v) => update('canonicalUrl', v)}
                            placeholder="https://yoursalon.lokacipro.com"
                        />
                    </section>

                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>Social sharing</h2>
                        <p className={styles.groupDesc}>Shown when your site is shared on Facebook, WhatsApp, or Twitter.</p>

                        <TextInput
                            label="Open Graph title"
                            value={form.ogTitle}
                            onChange={(v) => update('ogTitle', v)}
                            placeholder="Defaults to meta title if left blank"
                        />
                        <TextInput
                            label="Open Graph description"
                            value={form.ogDescription}
                            onChange={(v) => update('ogDescription', v)}
                            placeholder="Defaults to meta description if left blank"
                        />
                        <TextInput
                            label="Open Graph image URL"
                            value={form.ogImage}
                            onChange={(v) => update('ogImage', v)}
                            placeholder="https://example.com/social-share-image.jpg"
                            helperText="Recommended 1200×630px"
                        />
                        <SelectInput
                            label="Twitter card type"
                            value={form.twitterCard}
                            onChange={(v) => update('twitterCard', v)}
                            options={TWITTER_CARD_OPTIONS}
                        />
                    </section>

                    <section className={styles.fieldGroup}>
                        <h2 className={styles.groupTitle}>More SEO tools</h2>
                        <div className={styles.linkList}>
                            <Link to="/microsite/seo/pages" className={styles.toolLink}>
                                <FileSearch size={15} />
                                <span>Per-page SEO overrides</span>
                                <ArrowRight size={13} className={styles.toolLinkArrow} />
                            </Link>
                            <Link to="/microsite/seo/redirects" className={styles.toolLink}>
                                <ArrowRightLeft size={15} />
                                <span>URL redirects</span>
                                <ArrowRight size={13} className={styles.toolLinkArrow} />
                            </Link>
                            <Link to="/microsite/seo/sitemap" className={styles.toolLink}>
                                <Map size={15} />
                                <span>Sitemap</span>
                                <ArrowRight size={13} className={styles.toolLinkArrow} />
                            </Link>
                        </div>
                    </section>
                </div>

                <div className={styles.previewCol}>
                    <div className={styles.previewSticky}>
                        <MicrositeSerpPreview
                            title={form.metaTitle}
                            description={form.metaDescription}
                            url={micrositeStore?.subdomain ? `${micrositeStore.subdomain}.lokacipro.com` : 'yoursalon.lokacipro.com'}
                        />
                    </div>
                </div>
            </div>

            <MicrositeVersionDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                tabKey="seoConfig"
                storeIdentifier={activeStore?.storeIdentifier}
                vendorIdentifier={activeStore?.vendorIdentifier}
                onRolledBack={loadSeo}
            />
        </div>
    )
}

export default MicrositeSeo